import React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { PartnerClaim, PartnerClaimResource } from "../../api/partner_claims";
import { UserAdminInfo, UserResource } from "../../api/users";

/**
 * Shortest reason the backend will record. Mirrored here so the button can be
 * disabled rather than letting someone submit and be told no.
 */
const MIN_REASON_LENGTH = 15;

interface AttachClaimDialogProps {
  open: boolean;
  claim: PartnerClaim | undefined;
  onClose: () => void;
  onAttached: (message: string) => void;
}

/**
 * Attaches a paid-but-unclaimed purchase to an account.
 *
 * Two deliberate frictions. The account is **searched for and chosen**, never
 * inferred from the claim — the address on a payment is a delivery detail, and
 * the case this dialog exists for is precisely the one where it is wrong. And a
 * **reason is required**, because the audit entry serves a future reader who was
 * not on the call, and "customer request" tells them nothing.
 *
 * Nothing here is reversible from admin: it moves a subscription and grants a
 * paid membership.
 */
export const AttachClaimDialog: React.FC<AttachClaimDialogProps> = ({
  open,
  claim,
  onClose,
  onAttached,
}) => {
  const [term, setTerm] = React.useState("");
  const [candidates, setCandidates] = React.useState<UserAdminInfo[]>([]);
  const [truncated, setTruncated] = React.useState(false);
  const [selected, setSelected] = React.useState<UserAdminInfo | undefined>();
  const [reason, setReason] = React.useState("");
  const [searching, setSearching] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const [attaching, setAttaching] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTerm("");
    setCandidates([]);
    setTruncated(false);
    setSelected(undefined);
    setReason("");
    setSearched(false);
    setError(undefined);
  }, [open]);

  const handleSearch = async () => {
    if (!term.trim()) return;
    setSearching(true);
    setError(undefined);
    setSelected(undefined);
    try {
      // `auto` reads the term's shape, so an id, an address and a display name
      // all work without support having to say which they typed.
      const result = await UserResource.getList({
        q: term.trim(),
        searchBy: "auto",
      });
      setCandidates(result.users);
      // A display-name search can stop at a cap, and `nextToken` is not followed
      // here. Silence would be the dangerous answer: picking the "only" match
      // when the real account sat past the cap is the mistake this dialog exists
      // to prevent.
      setTruncated(Boolean(result.truncated) || Boolean(result.nextToken));
      setSearched(true);
    } catch (err) {
      // Cleared, not left standing: rows from the previous term under an error
      // banner are still selectable, and attaching to one of those would be
      // attaching to an account nobody searched for.
      setCandidates([]);
      setTruncated(false);
      setError(err instanceof Error ? err.message : "Account search failed.");
    } finally {
      setSearching(false);
    }
  };

  const handleAttach = async () => {
    if (!claim || !selected) return;
    setAttaching(true);
    setError(undefined);
    try {
      const result = await PartnerClaimResource.attachToAccount(selected.fsId!, {
        claimId: claim.claimId,
        reason: reason.trim(),
      });
      onAttached(
        `Attached to ${selected.email} — ${result.partnerName ?? result.partnerId} membership provisioned.`
      );
      onClose();
    } catch (err) {
      // Shown as-is. The 409s carry what support needs to act: which partner
      // blocks the attach, or that someone else already resolved it.
      setError(
        err instanceof Error ? err.message : "Could not attach the claim."
      );
    } finally {
      setAttaching(false);
    }
  };

  const reasonTooShort =
    reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH;
  // `fsId` is the app user id provisioning keys on; the Cognito `id` is a
  // different id space. An account without one has not finished signing up, so
  // there is nothing to attach a membership to — falling back to the Cognito id
  // would post an identifier the backend cannot resolve.
  const attachable = !!selected?.fsId;
  const canAttach =
    attachable && reason.trim().length >= MIN_REASON_LENGTH && !attaching;

  return (
    <Dialog open={open} onClose={attaching ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach purchase to an account</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            This grants a paid membership and moves the subscription. It cannot
            be undone from here. Attach only a purchase you have identified from
            the payment evidence — never by matching the email on the record.
          </Alert>

          {claim && (
            <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {claim.partnerName} · {claim.offerSlug}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid by {claim.buyerEmail ?? "no address on file"}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>
                {claim.claimId}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Find the account"
              placeholder="Email, display name, or user id"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              disabled={attaching}
            />
            <Button onClick={handleSearch} disabled={!term.trim() || searching}>
              {searching ? <CircularProgress size={18} /> : "Search"}
            </Button>
          </Box>

          {searched && candidates.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No account matched. The member may not have signed up yet — they
              need an account before a purchase can be attached to it.
            </Typography>
          )}

          {candidates.length > 0 && (
            <Stack gap={1}>
              <Typography variant="caption" color="text.secondary">
                Choose the account the member actually holds:
              </Typography>
              {truncated && (
                <Alert severity="warning">
                  More accounts matched than are shown. Narrow the search before
                  choosing — the one you want may not be in this list.
                </Alert>
              )}
              {candidates.map((user) => {
                const isSelected = selected?.id === user.id;
                return (
                  <Box
                    key={user.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${user.email}`}
                    aria-pressed={isSelected}
                    onClick={() => user.fsId && setSelected(user)}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && user.fsId) {
                        setSelected(user);
                      }
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      cursor: user.fsId ? "pointer" : "not-allowed",
                      opacity: user.fsId ? 1 : 0.6,
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      bgcolor: isSelected ? "action.selected" : undefined,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {user.displayName || user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    {!user.fsId && (
                      <Chip
                        label="sign-up not completed"
                        size="small"
                        color="error"
                        sx={{ mt: 0.5, mr: 0.5 }}
                      />
                    )}
                    {!user.revenueCatId && (
                      <Chip
                        label="no RevenueCat id"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Why — recorded on the audit trail"
            placeholder="What identified this purchase, and why this account."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={attaching}
            error={reasonTooShort}
            helperText={
              reasonTooShort
                ? "Say what evidence identified the purchase. A future reader has only this."
                : " "
            }
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={attaching}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleAttach}
          disabled={!canAttach}
          startIcon={attaching ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Attach purchase
        </Button>
      </DialogActions>
    </Dialog>
  );
};
